import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { useReveal } from "../hooks/useReveal";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "nuts", label: "Nuts" },
  { id: "chocolates", label: "Chocolates" },
  { id: "dry_fruits", label: "Dry Fruits" },
];

export default function Products() {
  const ref = useReveal();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "all";
  const [active, setActive] = useState(initialCat);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchProducts()
      .then((d) => setProducts(d))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (active === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", active);
    }
    setSearchParams(searchParams, { replace: true });
  }, [active]); // eslint-disable-line

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.category === active)),
    [active, products]
  );

  return (
    <div ref={ref} data-testid="products-page" className="pt-28 pb-24 bg-brand-ivory min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12 reveal">
          <span className="text-[11px] tracking-[0.32em] uppercase text-brand-gold font-semibold">
            The Catalogue
          </span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-brand-brown leading-tight">
            Our Premium Collection
          </h1>
          <p className="mt-4 text-brand-muted max-w-2xl mx-auto">
            Hand-selected nuts, chocolates, and dry fruits — sourced globally, curated locally.
            Tap any product to order instantly on WhatsApp.
          </p>
        </header>

        {/* Filter tabs */}
        <div
          data-testid="category-filter"
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12 reveal"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                active === c.id
                  ? "bg-brand-brown text-brand-ivory shadow-md"
                  : "bg-brand-beige/60 text-brand-brown hover:bg-brand-beige"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl bg-brand-beige/60 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-brand-muted" data-testid="no-products">
            No products in this category yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
